import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User } from '@/types';

interface AuthState {
  token: string | null;
  idToken: string | null;
  user: User | null;
  sessionId: string | null;
  expiresAt: number | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setToken: (token: string, expiresIn?: number) => void;
  setIdToken: (idToken: string) => void;
  setUser: (user: User) => void;
  login: (token: string, user: User, sessionId: string, expiresIn: number, idToken: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;

  // Token helpers
  isTokenExpiring: () => boolean;

  // Permission helpers
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  isAdminStcaria: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      idToken: null,
      user: null,
      sessionId: null,
      expiresAt: null,
      isAuthenticated: false,
      isLoading: true,

      setToken: (token, expiresIn) => {
        const expiresAt = expiresIn
          ? Date.now() + expiresIn * 1000
          : get().expiresAt;
        set({ token, expiresAt, isAuthenticated: !!token });
      },

      setIdToken: (idToken) => {
        set({ idToken });
      },

      setUser: (user) => {
        set({ user });
      },

      login: (token, user, sessionId, expiresIn, idToken) => {
        set({
          token,
          idToken,
          user,
          sessionId,
          expiresAt: Date.now() + expiresIn * 1000,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: () => {
        set({
          token: null,
          idToken: null,
          user: null,
          sessionId: null,
          expiresAt: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      setLoading: (isLoading) => {
        set({ isLoading });
      },

      isTokenExpiring: () => {
        const { expiresAt } = get();
        if (!expiresAt) return true;
        // Token is expiring if less than 2 minutes remain
        return Date.now() > expiresAt - 2 * 60 * 1000;
      },

      hasPermission: (permission) => {
        const { user } = get();
        return user?.permissions?.includes(permission) ?? false;
      },

      hasRole: (role) => {
        const { user } = get();
        return user?.roles?.includes(role) ?? false;
      },

      hasAnyPermission: (permissions) => {
        const { user } = get();
        if (!user?.permissions) return false;
        return permissions.some((p) => user.permissions.includes(p));
      },

      hasAllPermissions: (permissions) => {
        const { user } = get();
        if (!user?.permissions) return false;
        return permissions.every((p) => user.permissions.includes(p));
      },

      isAdminStcaria: () => {
        const { user } = get();
        return user?.roles?.includes('admin-stcaria') ?? false;
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      // Only persist user info, NOT the token (kept in memory for security)
      partialize: (state) => ({
        user: state.user,
        sessionId: state.sessionId,
        isAuthenticated: state.isAuthenticated,
        // Note: idToken is NOT persisted for security reasons
        // It is only stored in memory and used for Keycloak logout
      }),
    }
  )
);
